/**
 * Alpine.js data context for the search feedback ("Nicht das Richtige dabei?
 * Sag's uns!") in the video search (Partials/Video/Search.html, below
 * "Filter löschen").
 *
 * The component is nested inside the ymhVideoFilterBar component and reads
 * the filter bar's live state at the moment of submission — it never
 * re-implements the search. Captured:
 *   - NL query: window.__ymhNlSearch.query (set by natural-search.ts on a
 *     successful NL search, cleared on reset / "Filter löschen")
 *   - filters: exactly the values the filter bar sends to filterAction
 *     (see videoFilterBarDataContext._submit())
 *   - videos: UIDs of the first 15 cards currently rendered in the grid,
 *     including cards added via "mehr laden"/autoload (data-video-uid on
 *     Partials/Video/Card.html), in display order, plus the number of
 *     loaded cards
 *
 * The server verifies all IDs and resolves labels/metadata itself.
 * Submitting never touches the search UI or the result list.
 *
 * Agent-authored: video search feedback (2026-10).
 */

import { Alpine } from "@various/alpine";

const MIN_LENGTH = 3;
const MAX_LENGTH = 2000;
const MAX_QUERY_LENGTH = 250;
const MAX_TITLE_HINTS = 10;
const MAX_TITLE_HINT_LENGTH = 100;
const SWEAT_MAX = 10;
/** Max. videos captured per feedback (mirrors SearchFeedbackSubmissionParser::MAX_VIDEOS) */
const MAX_VIDEOS = 15;
/** Abort the request on stalled (mobile) connections */
const REQUEST_TIMEOUT_MS = 15000;
const GENERIC_ERROR = "Dein Feedback konnte gerade nicht gesendet werden. Bitte versuche es später noch einmal.";

type Status = "idle" | "submitting" | "success" | "error";

/** Subset of videoFilterBarDataContext state that describes the active search. */
interface FilterBarState {
  selectedTeachers: number[];
  selectedStyles: number[];
  selectedTopics: number[];
  selectedLevels: number[];
  selectedCategory: number;
  durMin: number | null;
  durMax: number | null;
  durDbMax: number;
  sweatMin: number | null;
  sweatMax: number | null;
  _nlTitleHints: string[];
  totalCount: number;
  _closeAll?: () => void;
}

interface FeedbackPayload {
  feedback: string;
  query: string;
  filters: {
    teachers: number[];
    styles: number[];
    topics: number[];
    levels: number[];
    category: number;
    durationMin: number | null;
    durationMax: number | null;
    /** Same rule as the filter bar's durLabel(): upper bound at DB max = "{min}+ Min" */
    durationOpenEnd: boolean;
    sweatMin: number | null;
    sweatMax: number | null;
    titleHints: string[];
  };
  videos: number[];
  /** Number of cards the user had loaded (may exceed videos.length) */
  loadedCount: number;
  totalCount: number;
  website: string;
}

function toIdList(values: unknown): number[] {
  if (!Array.isArray(values)) return [];
  const ids = values.map((v) => Math.trunc(Number(v))).filter((v) => Number.isFinite(v) && v > 0);
  return [...new Set(ids)];
}

function toIntOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? n : null;
}

/** Truncate by code points so emoji/surrogate pairs are never split (would break JSON on the server). */
function truncate(value: string, max: number): string {
  const chars = Array.from(value);
  return chars.length > max ? chars.slice(0, max).join("") : value;
}

function currentNlQuery(): string {
  const state = (window as any).__ymhNlSearch as { query?: string } | undefined;
  return String(state?.query ?? "").trim();
}

export function searchFeedbackDataContext() {
  return {
    open: false,
    status: "idle" as Status,
    text: "",
    /** Honeypot, bound to a visually hidden field */
    website: "",
    errorMessage: "",
    /** true when the error relates to the textarea content */
    textInvalid: false,
    /** Drives the existing .textarea-wrapper.focus style */
    textFocused: false,
    /** Mirrors window.__ymhNlSearch.query reactively for x-show */
    nlQuery: "",
    submitUrl: "",
    _filterBarEl: null as HTMLElement | null,
    _abort: null as AbortController | null,

    init(this: any) {
      const el = this.$el as HTMLElement;
      this.submitUrl = el.dataset.submitUrl ?? "";
      this._filterBarEl = el.closest(".ymh-filter-bar");
      this.nlQuery = currentNlQuery();

      document.addEventListener("ymh:nl-filters", ((e: CustomEvent) => {
        this.nlQuery = String(e.detail?.query ?? "").trim() || currentNlQuery();
      }) as EventListener);
      document.addEventListener("ymh:nl-reset", () => {
        this.nlQuery = "";
      });

      // ymh:nl-filters is not fired when the NL search fell back to "all
      // videos" (e.g. AI API error) — exactly when feedback is most useful.
      // Re-read the query whenever the result grid is replaced.
      const items = this._resultsRoot()?.querySelector(".tx-va-yoga-video-list-items .items");
      if (items) {
        new MutationObserver(() => {
          this.nlQuery = currentNlQuery();
        }).observe(items, { childList: true });
      }
    },

    /** The video list this filter bar belongs to (avoids picking up other lists on the page). */
    _resultsRoot(this: any): ParentNode | null {
      return (this._filterBarEl?.closest(".tx-va-yoga-video-list") as ParentNode | null) ?? document;
    },

    get remaining(): number {
      return Math.max(0, MAX_LENGTH - String(this.text).length);
    },

    /** German number format for the counter, e.g. "1.966" */
    get remainingLabel(): string {
      return this.remaining.toLocaleString("de-DE");
    },

    get isSubmitting(): boolean {
      return this.status === "submitting";
    },

    openModal(this: any) {
      // Filter popups (z-index 9000) would otherwise stay on top of the modal.
      const fb = (this._filterBarEl ? Alpine.$data(this._filterBarEl) : null) as unknown as FilterBarState | null;
      fb?._closeAll?.();

      this.status = "idle";
      this.errorMessage = "";
      this.textInvalid = false;
      this.open = true;
      const scroller = this.$refs.scroller as HTMLElement | undefined;
      if (scroller) scroller.scrollTop = 0;

      // Touch devices: show the whole dialog first (headline on top), without
      // the on-screen keyboard — it only opens when the user taps the field.
      // Focus goes to the headline so screen readers announce the dialog.
      // Desktop: focus the textarea directly.
      if (this._isTouch()) {
        this._focusWhenVisible(this.$refs.title as HTMLElement | undefined);
      } else {
        this._focusWhenVisible(this.$refs.feedbackText as HTMLElement | undefined);
      }
    },

    _isTouch(): boolean {
      return !!window.matchMedia?.("(pointer: coarse)").matches;
    },

    /**
     * x-show + x-transition only removes display:none on the next frame(s),
     * so focusing in $nextTick would hit a hidden element. Poll a few frames
     * until the element is rendered. (x-trap runs with .noautofocus so its
     * own initial focus does not move focus to the close button.)
     * preventScroll: our own scroll handling decides what is visible.
     */
    _focusWhenVisible(el: HTMLElement | undefined, attempts = 30) {
      if (!el) return;
      if (el.offsetParent !== null || el.getClientRects().length > 0) {
        el.focus({ preventScroll: true });
        return;
      }
      if (attempts > 0) {
        requestAnimationFrame(() => this._focusWhenVisible(el, attempts - 1));
      }
    },

    // ---- On-screen keyboard handling (mobile) -------------------------------
    //
    // Android Chrome / Samsung Internet / iOS Safari shrink only the *visual*
    // viewport when the keyboard opens; the fixed dialog stays full height,
    // so without help the lower part sits under the keyboard and cannot be
    // scrolled to. On touch devices we therefore follow visualViewport
    // *resize* (keyboard opens/closes):
    //   1. the scroll container gets bottom padding = keyboard height
    //      (container height − visible height), so the whole dialog can be
    //      scrolled (no inner scroll box). It depends only on the keyboard —
    //      not on focus (tapping "Feedback senden" blurs first and must not
    //      shift the layout) and not on panning offsets (stable while the
    //      user scrolls);
    //   2. the textarea is centred in the visible area ONCE, while the
    //      keyboard is opening (≤ 1 s after focus). Afterwards scrolling is
    //      entirely the user's — no re-centring on scroll events, which
    //      would fight the user's swipe (bug: could not scroll up again).

    _vvHandler: null as (() => void) | null,
    _vvTimer: 0 as number,
    /** Timestamp until which keyboard resizes may still re-centre the field */
    _centreUntil: 0 as number,

    _startViewportTracking(this: any) {
      const vv = window.visualViewport;
      if (!vv || this._vvHandler) return;
      const onResize = () => {
        window.clearTimeout(this._vvTimer);
        // let the browser finish its own focus scrolling / resize first
        this._vvTimer = window.setTimeout(
          () => this._adjustForKeyboard(Date.now() < this._centreUntil),
          60,
        );
      };
      this._vvHandler = onResize;
      vv.addEventListener("resize", onResize);
    },

    _stopViewportTracking(this: any) {
      const vv = window.visualViewport;
      if (vv && this._vvHandler) {
        vv.removeEventListener("resize", this._vvHandler);
      }
      this._vvHandler = null;
      this._centreUntil = 0;
      window.clearTimeout(this._vvTimer);
      const scroller = this.$refs.scroller as HTMLElement | undefined;
      if (scroller) scroller.style.paddingBottom = "";
    },

    onTextFocus(this: any) {
      this.textFocused = true;
      if (!this._isTouch()) return;
      this._startViewportTracking();
      this._centreUntil = Date.now() + 1000;
      // Keyboard animation takes ~250–400 ms; centre once it has settled,
      // even if the browser fires no resize event (keyboard already open).
      window.setTimeout(() => {
        if (Date.now() < this._centreUntil) this._adjustForKeyboard(true);
      }, 450);
    },

    onTextBlur(this: any) {
      this.textFocused = false;
    },

    _adjustForKeyboard(this: any, centre: boolean) {
      const scroller = this.$refs.scroller as HTMLElement | undefined;
      const field = this.$refs.feedbackText as HTMLElement | undefined;
      if (!scroller || !field || !this.open) return;

      const vv = window.visualViewport;
      const visibleHeight = vv ? vv.height : window.innerHeight;
      // Keyboard height = part of the full-height fixed container not visible
      const keyboard = Math.max(0, Math.round(scroller.clientHeight - visibleHeight));
      scroller.style.paddingBottom = keyboard > 0 ? `${keyboard + 16}px` : "";

      if (!centre || keyboard === 0 || document.activeElement !== field) return;
      // getBoundingClientRect() is relative to the layout viewport; the
      // visible area starts at visualViewport.offsetTop.
      const visibleTop = vv ? vv.offsetTop : 0;
      const rect = field.getBoundingClientRect();
      const delta = rect.top + rect.height / 2 - visibleTop - visibleHeight / 2;
      if (Math.abs(delta) > 4) {
        scroller.scrollTop += delta;
      }
    },

    closeModal(this: any) {
      if (!this.open) return;
      // Closing during a pending request cancels it (stalled mobile connections).
      if (this.status === "submitting") {
        this._abort?.abort();
        this.status = "idle";
      }
      this.open = false;
      this._stopViewportTracking();
      if (this.status === "success") {
        this.text = "";
        this.status = "idle";
      }
      // Safari does not focus buttons on click, so x-trap cannot restore
      // focus reliably — return it to the trigger explicitly.
      this.$nextTick(() => (this.$refs.trigger as HTMLElement | undefined)?.focus());
    },

    validate(this: any): boolean {
      const length = String(this.text).trim().length;
      if (length < MIN_LENGTH) {
        this.errorMessage = "Hoppla, da fehlt noch Dein Hinweis.";
        this.textInvalid = true;
        return false;
      }
      if (length > MAX_LENGTH) {
        this.errorMessage = `Dein Feedback darf höchstens ${MAX_LENGTH} Zeichen lang sein.`;
        this.textInvalid = true;
        return false;
      }
      this.errorMessage = "";
      this.textInvalid = false;
      return true;
    },

    /** Snapshot of the search exactly as currently displayed. */
    collectPayload(this: any): FeedbackPayload {
      const fb = (this._filterBarEl ? Alpine.$data(this._filterBarEl) : null) as unknown as FilterBarState | null;

      const durMin = fb ? toIntOrNull(fb.durMin) : null;
      const sweatMin = fb ? toIntOrNull(fb.sweatMin) : null;
      // Same defaults for the upper bound as videoFilterBarDataContext._submit()
      const durMax = fb && durMin !== null ? toIntOrNull(fb.durMax ?? fb.durDbMax) : null;
      const sweatMax = fb && sweatMin !== null ? toIntOrNull(fb.sweatMax ?? SWEAT_MAX) : null;
      const durationOpenEnd = !!fb && durMax !== null && durMax >= Number(fb.durDbMax);

      const titleHints = (fb?._nlTitleHints ?? [])
        .map((h) => truncate(String(h).trim(), MAX_TITLE_HINT_LENGTH))
        .filter((h) => h !== "")
        .slice(0, MAX_TITLE_HINTS);

      // All cards loaded so far (initial page + "mehr laden"/autoload), in
      // display order. Duplicates are kept on purpose: NL load-more
      // re-interprets the query per page and may repeat videos.
      const loadedUids = Array.from(
        (this._resultsRoot() as ParentNode).querySelectorAll<HTMLElement>(
          ".tx-va-yoga-video-list-items .items [data-video-uid]",
        ),
        (card) => Math.trunc(Number(card.dataset.videoUid)),
      ).filter((uid) => Number.isFinite(uid) && uid > 0);
      const videoUids = loadedUids.slice(0, MAX_VIDEOS);

      return {
        feedback: String(this.text).trim(),
        query: truncate(currentNlQuery(), MAX_QUERY_LENGTH),
        filters: {
          teachers: toIdList(fb?.selectedTeachers),
          styles: toIdList(fb?.selectedStyles),
          topics: toIdList(fb?.selectedTopics),
          levels: toIdList(fb?.selectedLevels).filter((l) => l <= 3).sort(),
          category: Math.max(0, Math.trunc(Number(fb?.selectedCategory ?? 0)) || 0),
          durationMin: durMin,
          durationMax: durMax,
          durationOpenEnd,
          sweatMin,
          sweatMax,
          titleHints: [...new Set(titleHints)],
        },
        videos: videoUids,
        loadedCount: loadedUids.length,
        totalCount: Math.max(0, Math.trunc(Number(fb?.totalCount ?? 0)) || 0),
        website: String(this.website),
      };
    },

    async submit(this: any) {
      if (this.status === "submitting") return;
      if (!this.validate()) {
        this._focusWhenVisible(this.$refs.feedbackText as HTMLElement | undefined);
        return;
      }

      this.status = "submitting";
      this.errorMessage = "";

      const abort = new AbortController();
      this._abort = abort;
      const timeout = window.setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(this.submitUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(this.collectPayload()),
          signal: abort.signal,
        });

        let data: { ok?: boolean; error?: string; message?: string } | null = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (response.ok && data?.ok) {
          this.status = "success";
          const scroller = this.$refs.scroller as HTMLElement | undefined;
          if (scroller) scroller.scrollTop = 0;
          this._focusWhenVisible(this.$refs.successTitle as HTMLElement | undefined);
          return;
        }

        this.status = "error";
        this.errorMessage = data?.message || GENERIC_ERROR;
        this.textInvalid = String(data?.error ?? "").startsWith("feedback_");
        this._focusWhenVisible(this.$refs.feedbackText as HTMLElement | undefined);
      } catch {
        // Closed by the user while pending → modal is gone, nothing to show.
        if (!this.open) return;
        this.status = "error";
        this.errorMessage = GENERIC_ERROR;
        this.textInvalid = false;
        this._focusWhenVisible(this.$refs.feedbackText as HTMLElement | undefined);
      } finally {
        window.clearTimeout(timeout);
        if (this._abort === abort) this._abort = null;
      }
    },
  };
}
